/**
 * FrugalCurtain.jsx - Cortina decorativa del dashboard
 * Se abre hacia arriba como una persiana de negocio
 * Muestra ilustración estilo sketch + frase frugal
 * Carga configuración desde backend si existe, sino usa defaults
 * Cambia imagen/frase cada vez que se cierra la cortina
 * Soporta i18n - frases en español e inglés
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { FRUGAL_QUOTES } from '../data/frugalQuotes';
import { FRUGAL_QUOTES_EN } from '../data/frugalQuotes_en';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Imágenes predeterminadas
const DEFAULT_IMAGES = [
  'https://customer-assets.emergentagent.com/job_52b9527d-3c1a-4572-bf81-2e6452d816a6/artifacts/44nejtgr_24f6c653-3df2-43e4-8008-96400c7635a1-2.png',
];

const FrugalCurtain = ({ isOpen, onToggle, isTrial = false, token, t, language = 'es' }) => {
  const [quote, setQuote] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const allImagesRef = useRef([...DEFAULT_IMAGES]);
  const allQuotesRef = useRef([]);
  const configLoadedRef = useRef(false);
  const currentLanguageRef = useRef(language);

  // Obtener frases según idioma
  const getDefaultQuotes = (lang) => {
    return lang === 'en' ? FRUGAL_QUOTES_EN : FRUGAL_QUOTES;
  };

  // Cargar configuración al inicio y cuando cambia el idioma
  useEffect(() => {
    currentLanguageRef.current = language;
    loadCurtainConfig();
  }, [language]);

  // Cambiar imagen/frase cada vez que se abre la cortina
  useEffect(() => {
    if (isOpen && configLoadedRef.current) {
      selectRandomContent();
    }
  }, [isOpen]);

  const loadCurtainConfig = async () => {
    const lang = currentLanguageRef.current;
    let images = [...DEFAULT_IMAGES];
    let quotes = [...getDefaultQuotes(lang)];

    if (token) {
      try {
        const res = await fetch(`${API_URL}/api/v2/frugal/curtain/config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const config = await res.json();
          if (config.images && config.images.length > 0) {
            const customImages = config.images.filter(img => img.active).map(img => 
              img.url.startsWith('/') ? `${API_URL}${img.url}` : img.url
            );
            images = [...images, ...customImages];
          }
          if (config.quotes && config.quotes.length > 0) {
            const customQuotes = config.quotes.filter(q => q.active);
            quotes = [...quotes, ...customQuotes];
          }
        }
      } catch (err) {
        console.log('Using default curtain content');
      }
    }

    allImagesRef.current = images;
    allQuotesRef.current = quotes;
    configLoadedRef.current = true;
    
    // Seleccionar contenido inicial
    selectRandomContent();
  };

  const selectRandomContent = () => {
    const images = allImagesRef.current;
    const quotes = allQuotesRef.current;
    
    const randomImage = images[Math.floor(Math.random() * images.length)];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    
    setImageUrl(randomImage);
    setQuote(randomQuote);
  };

  if (isOpen) {
    // Cortina abierta - mostrar solo el botón para cerrar
    return (
      <button
        onClick={onToggle}
        className="w-full py-2 flex items-center justify-center gap-2 
                   bg-emerald-900/30 hover:bg-emerald-900/50 
                   border border-emerald-500/20 rounded-xl
                   text-emerald-300/70 text-sm transition-all duration-300"
        data-testid="curtain-close-btn"
      >
        <ChevronUp size={16} />
        <span>{t?.('frugal.curtain.hide') || 'Ocultar detalles'}</span>
        <ChevronUp size={16} />
      </button>
    );
  }

  // Cortina cerrada - mostrar imagen y frase
  return (
    <div 
      className="relative overflow-hidden rounded-2xl border border-emerald-500/20
                 shadow-xl shadow-emerald-900/20
                 animate-slideDown"
      data-testid="frugal-curtain"
    >
      {/* Imagen con fondo verde claro para PNG transparente */}
      <div className="relative h-64 overflow-hidden bg-emerald-50 flex items-center justify-center">
        {imageUrl && (
          <img 
            src={imageUrl} 
            alt="Frugal lifestyle"
            className="max-w-full max-h-full object-contain p-2"
          />
        )}
        
        {/* Gradiente sutil en la parte inferior para el texto */}
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-emerald-100 via-emerald-50/95 to-transparent" />
        
        {/* Contenido sobre la imagen */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Frase */}
          {quote && (
            <div className="mb-1">
              <p className="text-slate-700 text-base font-light italic leading-snug mb-1">
                "{quote.text}"
              </p>
              <p className="text-emerald-700 text-xs font-medium">
                — {quote.author}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Botón para abrir/ver detalles */}
      <button
        onClick={onToggle}
        className="w-full py-3 flex items-center justify-center gap-2
                   bg-emerald-700/80 hover:bg-emerald-600
                   text-white text-sm font-medium
                   transition-all duration-300 group"
        data-testid="curtain-open-btn"
      >
        <ChevronDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
        <span>
          {isTrial 
            ? (t?.('frugal.curtain.viewDetailsTrial') || 'Ver detalles de tu presupuesto')
            : (t?.('frugal.curtain.viewDetails') || 'Ver detalles')
          }
        </span>
        <ChevronDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
      </button>
    </div>
  );
};

export default FrugalCurtain;
